import { AnimatePresence } from 'framer-motion';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { SidebarModal } from '../../../components';
import { AnimatedTab } from '../../../components/animation/AnimatedTab';
import Spinner from '../../../components/common/Spinner';
import {
  dateDifferenceInDaysFromToday,
  tokens,
  wallets
} from '../../../helpers/helperFunctions';
import { WalletsType } from '../../../types/types';
import { PurchaseSteps } from '../Maeketplace.types';
import { purchasePackage } from '../Marketplace.actions';
import { Initial } from './Initial';
import { PayMixed } from './PayMixed';
import { PayTether } from './PayTether';
import { PaymentMethod } from './PaymentMethod';
import { Success } from './Success';
import { getLicenses } from '../../Settings/Settings.actions';

interface PurchasePackageAndComboProps {
  selectedPackage: any;
  modal: boolean;
  setModal: Dispatch<SetStateAction<boolean>>;
  userDiscountCodeId?: number | null;
}

export const PurchasePackageAndCombo = ({
  selectedPackage,
  setModal,
  modal,
  userDiscountCodeId = null
}: PurchasePackageAndComboProps) => {
  const dispatch = useAppDispatch();
  const { addHubLoading } = useAppSelector((state) => state.marketplace);
  const { loading, licences } = useAppSelector((state) => state.settings);
  const { userInfo } = useAppSelector((state) => state.auth);

  const [step, setStep] = useState<PurchaseSteps>(PurchaseSteps.PAYMENT_METHOD);
  const [selectedLicence, setSelectedLicence] = useState<any>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<any>(wallets[0]);
  const [selectedToken, setSelectedToken] = useState<any>();

  const [lp, setLp] = useState('');
  const [club, setClub] = useState('');
  const [usdt, setUsdt] = useState('');
  const [usdc, setUsdc] = useState('');
  const [cash, setCash] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState('');

  const isRestrictedUser = userInfo.userType === 2;

  const licenceDate = dateDifferenceInDaysFromToday(
    userInfo.user.currentVirtualOfficeLicence?.dateEnd
  );

  const renderTitle = handleSidebarTitle(step);

  const initialPlan = licences?.[0];

  useEffect(() => {
    dispatch(getLicenses());

    if (licenceDate <= 30) {
      if (isRestrictedUser) {
        setStep(PurchaseSteps.PAYMENT_METHOD);
      } else {
        if (
          selectedPackage?.packageId === 1 ||
          selectedPackage?.packageId === 2 ||
          selectedPackage?.packageId === 311 ||
          selectedPackage?.packageId === 312 ||
          selectedPackage?.packageId === 313 ||
          selectedPackage?.packageId === 314 ||
          selectedPackage?.packageId === 315
        ) {
          setStep(PurchaseSteps.PAYMENT_METHOD);
        } else {
          setSelectedLicence(initialPlan);
          setStep(PurchaseSteps.INITIAL);
        }
      }
    } else {
      setStep(PurchaseSteps.PAYMENT_METHOD);
    }

    setLp('');
    setCash('');
    setClub('');
    setUsdt('');
    setUsdc('');
    setSelectedToken('');
    setSelectedLicence(null);
    setCalculatedPrice('');
  }, [modal, isRestrictedUser, selectedPackage]);

  const handlePay = () => {
    dispatch(
      purchasePackage({
        packageId: selectedPackage.packageId,
        licenceId: selectedLicence?.id || null,
        lp: Number(lp),
        club: Number(club),
        cash: Number(cash),
        usdt: Number(usdt),
        usdc: Number(usdc),
        networkType: selectedNetwork.networkType,
        userDiscountCodeId,
        handleSuccess: () => setStep(PurchaseSteps.SUCCESS)
      })
    );
  };

  return (
    <SidebarModal
      isOpen={modal}
      sidebarWidth={'650px'}
      setOpen={(v) => {
        setModal(v);
      }}
      title={renderTitle}>
      <AnimatePresence exitBeforeEnter>
        {(loading || addHubLoading) && <Spinner absolute transparent />}

        {step === PurchaseSteps.INITIAL && (
          <AnimatedTab key={PurchaseSteps.INITIAL}>
            <Initial
              setStep={setStep}
              selectedPackage={selectedPackage}
              setModal={setModal}
              setSelectedLicence={setSelectedLicence}
              selectedLicence={selectedLicence}
              licenceDate={licenceDate}
            />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.PAYMENT_METHOD && (
          <AnimatedTab key={PurchaseSteps.PAYMENT_METHOD}>
            <PaymentMethod
              setStep={setStep}
              selectedPackage={selectedPackage}
              selectedLicence={selectedLicence}
              setUsdt={setUsdt}
              setUsdc={setUsdc}
            />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.PAY_MIXED && (
          <AnimatedTab key={PurchaseSteps.PAY_MIXED}>
            <PayMixed
              setStep={setStep}
              selectedPackage={selectedPackage}
              selectedLicence={selectedLicence}
              lp={lp}
              club={club}
              usdt={usdt}
              cash={cash}
              calculatedPrice={calculatedPrice}
              setLp={setLp}
              setClub={setClub}
              setCalculatedPrice={setCalculatedPrice}
              setCash={setCash}
              handlePay={handlePay}
              userDiscountCodeId={userDiscountCodeId}
            />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.PAY_TETHER && (
          <AnimatedTab key={PurchaseSteps.PAY_TETHER}>
            <PayTether
              setStep={setStep}
              selectedPackage={selectedPackage}
              selectedLicence={selectedLicence}
              selectedNetwork={selectedNetwork}
              selectedToken={selectedToken}
              setSelectedToken={setSelectedToken}
              setSelectedNetwork={setSelectedNetwork}
              setUsdt={setUsdt}
              setUsdc={setUsdc}
              lp={lp}
              club={club}
              cash={cash}
              calculatedPrice={calculatedPrice}
              handlePay={handlePay}
            />
          </AnimatedTab>
        )}

        {step === PurchaseSteps.SUCCESS && (
          <AnimatedTab key={PurchaseSteps.SUCCESS}>
            <Success
              selectedPackage={selectedPackage}
              calculatedPrice={calculatedPrice}
              selectedToken={selectedToken}
              selectedLicence={selectedLicence}
            />
          </AnimatedTab>
        )}
      </AnimatePresence>
    </SidebarModal>
  );
};

const handleSidebarTitle = (step: PurchaseSteps) => {
  const { t } = useTranslation('marketplace');

  switch (step) {
    case PurchaseSteps.INITIAL:
      return t('backoffice_combo');
    case PurchaseSteps.PAYMENT_METHOD:
      return t('select_package');
    case PurchaseSteps.PAY_MIXED:
      return t('select_package');
    case PurchaseSteps.PAY_TETHER:
      return t('select_package');
    case PurchaseSteps.SUCCESS:
      return t('order_confirmation');

    default:
      return '';
  }
};
